import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { paymentService } from '../../../services/tenant/api/paymentService';
import { commissionWalletPaymentService } from '../../../services/tenant/api/commissionWalletPaymentService';

const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

// Pick the right payment service based on payment type
const getPaymentService = (type) => (
    type === 'commission_wallet' ? commissionWalletPaymentService : paymentService
);

const PaymentGateway = () => {
    const [searchParams] = useSearchParams();
    const { companyName } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        const token = searchParams.get('token');
        const type = searchParams.get('type') || 'credit_recharge';

        if (!token) {
            setError('Invalid payment request. No payment token found.');
            setLoading(false);
            return;
        }

        let createdScript = null;

        // The Razorpay <script> is shared and persistent: a listener bound to a
        // node this effect did not create outlives the node's removal, because
        // the node is never removed (#219). Tracked so cleanup can unbind it.
        let existingScript = null;

        // Every path below ends in setState. Once the effect is torn down those
        // setters belong to an unmounted component, and `start` also opens the
        // Razorpay checkout — so a load event that lands after the user has
        // navigated away would pop a payment modal on whatever page they are on
        // now. Guarding here, rather than only unbinding, covers the created-
        // script path too: removing a <script> node does not reliably cancel a
        // load already in flight.
        let cancelled = false;

        const start = () => {
            if (cancelled) return;
            initializePayment(token, type);
        };

        if (window.Razorpay) {
            // Already loaded
            start();
        } else {
            const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
            if (existing) {
                existingScript = existing;
                existing.addEventListener('load', start);
            } else {
                const script = document.createElement('script');
                script.src = RAZORPAY_SCRIPT_SRC;
                script.async = true;
                script.onload = start;
                script.onerror = () => {
                    if (cancelled) return;
                    setError('Failed to load payment gateway. Please check your internet connection and try again.');
                    setLoading(false);
                };
                document.body.appendChild(script);
                createdScript = script;
            }
        }

        return () => {
            cancelled = true;

            if (existingScript) {
                existingScript.removeEventListener('load', start);
                existingScript = null;
            }

            if (createdScript && document.body.contains(createdScript)) {
                document.body.removeChild(createdScript);
            }
        };
    }, [searchParams, companyName]);

    const initializePayment = async (token, type) => {
        try {
            const service = getPaymentService(type);
            const contextResult = await service.decryptToken(token);

            if (!contextResult.success) {
                throw new Error(contextResult.error || 'Invalid payment token');
            }

            const context = contextResult.context;

            // Compare token company vs the URL param, normalising encoding
            // artifacts: legacy URLs used urlencode (space -> '+') which the
            // router does not turn back into a space. Treat '+' as space and
            // compare trimmed + case-insensitive so a benign encoding/format
            // difference never blocks a legitimate payment.
            const normalize = (s) => String(s ?? '').replace(/\+/g, ' ').trim().toLowerCase();
            if (normalize(context.tenant_company_name) !== normalize(companyName)) {
                throw new Error('Payment verification failed. Company name mismatch.');
            }

            setLoading(false);
            startRazorpayPayment(context, type);
        } catch (err) {
            setError(err?.message || 'Failed to initialize payment');
            setLoading(false);
        }
    };

    const startRazorpayPayment = (context, type) => {
        if (!window.Razorpay) {
            setError('Payment gateway not loaded. Please refresh and try again.');
            return;
        }

        const description = type === 'commission_wallet'
            ? `Add Money - ₹${context.amount}`
            : `Credit Recharge - ${context.credits} Credits`;

        const options = {
            key: context.razorpay_key,
            amount: context.amount * 100,
            currency: 'INR',
            name: `${companyName} - Secure Payment`,
            description,
            order_id: context.order_id,
            handler: (razorpayResponse) => handlePaymentSuccess(razorpayResponse, context, type),
            modal: {
                ondismiss: () => handlePaymentCancel(context),
            },
            theme: {
                // Razorpay needs a resolved hex at init; this matches the app primary
                color: '#3B82F6',
            },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
    };

    const handlePaymentSuccess = async (razorpayResponse, context, type) => {
        try {
            const service = getPaymentService(type);
            const result = await service.confirmPayment({
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
                payment_confirm_token: context.payment_confirm_token,
            });

            if (result.success) {
                setPaymentComplete(true);
                setSuccessMessage(
                    type === 'commission_wallet'
                        ? `Payment successful! ₹${result.data.amount_added} added to wallet.`
                        : `Payment successful! ${result.data.credits_added} credits added.`
                );

                setTimeout(() => {
                    window.location.href = `${context.return_url}?payment_status=success&payment_message=${encodeURIComponent('Payment completed successfully')}`;
                }, 3000);
            } else {
                setError(result.message || 'Payment confirmation failed');
            }
        } catch (err) {
            setError(err?.message || 'Payment confirmation failed');
        }
    };

    const handlePaymentCancel = (context) => {
        window.location.href = `${context.return_url}?payment_status=cancelled&payment_message=${encodeURIComponent('Payment was cancelled')}`;
    };

    const pageStyle = {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-text) 100%)',
        padding: '2rem 1rem',
    };

    const TrustBadges = () => (
        <div className="flex flex-wrap align-items-center justify-content-center gap-2 mt-4">
            <Tag severity="success" icon="pi pi-lock" value="256-bit SSL" />
            <Tag severity="info" icon="pi pi-verified" value="PCI DSS" />
            <Tag severity="warning" icon="pi pi-shield" value="Razorpay Secured" />
        </div>
    );

    return (
        <div style={pageStyle} className="flex align-items-center justify-content-center">
            <div className="w-full max-w-2xl">
                {paymentComplete ? (
                    <Card className="shadow-8">
                        <div className="text-center p-4">
                            <i
                                className="pi pi-check-circle text-green-500 pi-4x mb-3 block"
                                aria-hidden="true"
                            />
                            <h2 className="text-green-600 text-2xl font-semibold m-0 mb-3">
                                Payment Successful!
                            </h2>
                            <Message severity="success" text={successMessage} className="w-full mb-3" />
                            <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} strokeWidth="4" />
                            <p className="text-600 text-sm m-0 mt-3">
                                Redirecting you back in a few seconds...
                            </p>
                        </div>
                    </Card>
                ) : error ? (
                    <Card className="shadow-8">
                        <div className="text-center p-4">
                            <i
                                className="pi pi-exclamation-triangle text-red-500 pi-4x mb-3 block"
                                aria-hidden="true"
                            />
                            <h2 className="text-red-600 text-2xl font-semibold m-0 mb-3">
                                Payment Error
                            </h2>
                            <Message severity="error" text={error} className="w-full mb-3" />
                            <Divider />
                            <Button
                                label="Go Back"
                                icon="pi pi-arrow-left"
                                severity="secondary"
                                onClick={() => window.history.back()}
                            />
                        </div>
                    </Card>
                ) : (
                    <Card className="shadow-8">
                        <div className="text-center p-4">
                            <i
                                className="pi pi-shield text-primary pi-4x mb-3 block"
                                aria-hidden="true"
                            />
                            <h2 className="text-primary text-2xl font-semibold m-0 mb-3">
                                {loading ? 'Secure Payment Gateway' : 'Opening Secure Payment Gateway'}
                            </h2>
                            <ProgressSpinner style={{ width: '3rem', height: '3rem' }} strokeWidth="4" />
                            <h3 className="text-900 text-lg font-medium m-0 mt-3 mb-2">
                                {loading ? 'Initializing Payment System' : 'Please wait...'}
                            </h3>
                            <p className="text-600 text-sm line-height-3 m-0 mb-3">
                                {loading
                                    ? <>Setting up secure payment environment for <strong>{companyName}</strong></>
                                    : 'Payment gateway will open automatically'}
                            </p>
                            <Message
                                severity={loading ? 'warn' : 'info'}
                                text={loading
                                    ? 'Please do not close this window during the payment process'
                                    : 'Please complete the payment in the popup window that opens'}
                                className="w-full"
                            />
                            <TrustBadges />
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default PaymentGateway;
